import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface EntryRemovalFailure {
  entryKey: string;
  entryName: string;
  removed: number;
  total: number;
  remainingIds: string[];
}

export const useEntryRemoval = (
  removeOne: (inviteId: string) => Promise<unknown>,
) => {
  const running = useRef<string | null>(null);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [failure, setFailure] = useState<EntryRemovalFailure | null>(null);

  const removeEntry = async (
    entryKey: string,
    entryName: string,
    inviteIds: string[],
  ) => {
    if (running.current != null) return;
    running.current = entryKey;
    setRemovingKey(entryKey);
    const total =
      failure?.entryKey === entryKey ? failure.total : inviteIds.length;
    const removedBefore = total - inviteIds.length;
    setFailure(null);
    try {
      for (const [index, inviteId] of inviteIds.entries()) {
        try {
          await removeOne(inviteId);
        } catch {
          setFailure({
            entryKey,
            entryName,
            removed: removedBefore + index,
            total,
            remainingIds: inviteIds.slice(index),
          });
          return;
        }
      }
    } finally {
      running.current = null;
      setRemovingKey(null);
    }
  };

  const retry = () => {
    if (failure)
      void removeEntry(
        failure.entryKey,
        failure.entryName,
        failure.remainingIds,
      );
  };

  return { removingKey, failure, removeEntry, retry };
};

export const EntryRemovalFailureAlert = ({
  failure,
  isRetrying,
  onRetry,
}: {
  failure: EntryRemovalFailure;
  isRetrying: boolean;
  onRetry: () => void;
}) => {
  const { t } = useTranslation();
  const remaining = failure.total - failure.removed;
  return (
    <Alert
      status="error"
      rounded="md"
      fontSize="sm"
      alignItems="flex-start"
      mb="4"
    >
      <AlertIcon />
      <Box>
        <AlertDescription>
          {failure.removed === 0
            ? t(
                "{{organization}} could not be removed. No addresses were removed.",
                {
                  organization: failure.entryName,
                },
              )
            : t(
                "Not every address at {{organization}} could be removed. {{removed}} of {{total}} are removed, and {{remaining}} still have access.",
                {
                  organization: failure.entryName,
                  removed: failure.removed,
                  total: failure.total,
                  remaining,
                },
              )}
        </AlertDescription>
        <Button
          size="sm"
          mt="2"
          colorScheme="red"
          variant="outline"
          isLoading={isRetrying}
          onClick={onRetry}
        >
          {t("Try again")}
        </Button>
      </Box>
    </Alert>
  );
};
